/* Control de EPP: registro de entregas (una fila por ítem entregado, como la
 * hoja "Registro EPP" de la planilla), con filtros y el detalle de cada entrega. */
import { IonButton, IonContent, IonPage } from "@ionic/react";
import { FormControlLabel, MenuItem, Switch, TextField } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "src/components/Header";
import {
  AnticipadaInfo,
  BuscadorFuncionario,
  Cargando,
  DetalleEntregaDialog,
  EstadoChip,
  useRolEpp,
  useToastEpp,
} from "src/components/epp/EppComunes";
import EppAPI, {
  ESTADOS_FILTRO,
  EstadoEpp,
  FiltroEntregas,
  Funcionario,
  LineaEntrega,
  etiquetaItem,
  fechaCorta,
  plural,
} from "src/services/epp";
import "./Epp.css";

const EppRegistro: React.FC = () => {
  const rol = useRolEpp();
  const toast = useToastEpp();
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const [depositoId, setDepositoId] = useState<number | "">("");
  const [funcionario, setFuncionario] = useState<Funcionario | null>(null);
  const [estado, setEstado] = useState<EstadoEpp | "">((params.get("estado") as EstadoEpp) || "");
  const [soloVigentes, setSoloVigentes] = useState(params.get("vigentes") === "1");
  const [soloAnticipadas, setSoloAnticipadas] = useState(params.get("anticipadas") === "1");

  // La pantalla puede seguir montada al volver con otro filtro en la URL (los
  // indicadores del inicio): se toman los de la URL cada vez que cambia.
  useEffect(() => {
    const p = new URLSearchParams(search);
    setEstado((p.get("estado") as EstadoEpp) || "");
    setSoloVigentes(p.get("vigentes") === "1");
    setSoloAnticipadas(p.get("anticipadas") === "1");
  }, [search]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [filas, setFilas] = useState<LineaEntrega[] | null>(null);
  const [entregaId, setEntregaId] = useState<number | null>(null);

  const buscar = async () => {
    setFilas(null);
    const filtro: FiltroEntregas = {};
    if (depositoId) filtro.deposito_id = depositoId;
    if (funcionario) filtro.cedula = funcionario.cedula;
    if (estado) filtro.estado = estado;
    if (soloVigentes) filtro.vigentes = 1;
    if (soloAnticipadas) filtro.anticipadas = 1;
    if (desde) filtro.desde = desde;
    if (hasta) filtro.hasta = hasta;
    try {
      setFilas(await EppAPI.entregas(filtro));
    } catch (err) {
      setFilas([]);
      toast.error(err, "No se pudieron cargar las entregas");
    }
  };

  // Una tarjeta por entrega (datos del colaborador una sola vez) con sus líneas,
  // en el mismo orden en que llegan (de la más nueva a la más vieja).
  const entregas = useMemo(() => {
    const grupos: LineaEntrega[][] = [];
    const porId: Record<number, LineaEntrega[]> = {};
    (filas || []).forEach((f) => {
      if (!porId[f.EntregaId]) grupos.push((porId[f.EntregaId] = []));
      porId[f.EntregaId].push(f);
    });
    return grupos;
  }, [filas]);

  useEffect(() => {
    buscar();
  }, [depositoId, funcionario, estado, soloVigentes, soloAnticipadas, desde, hasta]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <IonPage>
      <Header titulo="Registro de entregas de EPP" />
      <IonContent id="epp-registro">
        <div className="epp-page campos-mui">
          <div className="epp-card">
            <div className="epp-barra">
              <TextField
                select
                label="Depósito"
                variant="outlined"
                size="small"
                value={depositoId}
                onChange={(e) => setDepositoId(e.target.value === "" ? "" : Number(e.target.value))}
                style={{ flex: "1 1 200px" }}
              >
                <MenuItem value="">Todos</MenuItem>
                {(rol?.depositos || []).map((d) => (
                  <MenuItem key={d.Id} value={d.Id}>{d.Nombre}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Estado"
                variant="outlined"
                size="small"
                value={estado}
                onChange={(e) => setEstado(e.target.value as EstadoEpp | "")}
                style={{ flex: "1 1 200px" }}
              >
                <MenuItem value="">Todos</MenuItem>
                {ESTADOS_FILTRO.map((e) => (
                  <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                ))}
              </TextField>
            </div>
            <BuscadorFuncionario valor={funcionario} onSelect={setFuncionario} label="Filtrar por colaborador" />
            <div className="epp-barra" style={{ marginTop: 8, marginBottom: 0 }}>
              <TextField label="Desde" type="date" variant="outlined" size="small" InputLabelProps={{ shrink: true }}
                style={{ flex: "1 1 140px" }} value={desde} onChange={(e) => setDesde(e.target.value)} />
              <TextField label="Hasta" type="date" variant="outlined" size="small" InputLabelProps={{ shrink: true }}
                style={{ flex: "1 1 140px" }} value={hasta} onChange={(e) => setHasta(e.target.value)} />
            </div>
            <div className="epp-barra" style={{ marginTop: 4, marginBottom: 0 }}>
              <FormControlLabel
                control={<Switch color="primary" checked={soloVigentes} onChange={(e) => setSoloVigentes(e.target.checked)} />}
                label="Solo vigentes"
              />
              <FormControlLabel
                control={<Switch color="primary" checked={soloAnticipadas} onChange={(e) => setSoloAnticipadas(e.target.checked)} />}
                label="Solo retiros anticipados"
              />
            </div>
          </div>

          {filas === null && <Cargando />}
          {filas?.length === 0 && <div className="epp-vacio">No hay entregas con esos filtros.</div>}
          {filas && filas.length >= 500 && (
            <div className="epp-aviso">Se muestran las 500 más recientes: acotá los filtros para ver el resto.</div>
          )}
          {entregas.map((lineas) => {
            const e = lineas[0];
            return (
              <div key={e.EntregaId} className={`epp-card ${e.Anulada ? "epp-anulada" : ""}`}>
                <p className="epp-card-titulo">{e.Nombre}</p>
                <div className="epp-meta">
                  <span>CI {e.Cedula}</span>
                  {e.Departamento && <span>{e.Departamento}</span>}
                  {e.Cargo && <span>{e.Cargo}</span>}
                </div>
                <div className="epp-meta">
                  <span>Entregado {fechaCorta(e.Fecha)}</span>
                  <span>{e.Deposito}</span>
                  <span>Entregó {e.EntregadoPorNombre}</span>
                </div>
                <div style={{ marginTop: 6 }}>
                  {lineas.map((f) => (
                    <div key={f.Id} className="epp-fila">
                      <div className="epp-fila-cabecera">
                        <span className="epp-item">{f.Cantidad} × {etiquetaItem(f.Item, f.Talle)}</span>
                        <EstadoChip estado={f.Estado} etiqueta={f.EstadoEtiqueta} />
                      </div>
                      <div className="epp-meta">
                        <span>Reposición {fechaCorta(f.FechaReposicion)}</span>
                        {f.DiasRestantes !== null && f.DiasRestantes >= 0 && (
                          <span>Faltan {plural(f.DiasRestantes, "día", "días")}</span>
                        )}
                      </div>
                      {f.Anticipada && <AnticipadaInfo motivo={f.Motivo} estado={f.Estado} />}
                    </div>
                  ))}
                </div>
                <IonButton size="small" fill="clear" onClick={() => setEntregaId(e.EntregaId)}>
                  Ver entrega
                </IonButton>
              </div>
            );
          })}
        </div>
        <DetalleEntregaDialog
          entregaId={entregaId}
          puedeAnular={Boolean(rol?.admin)}
          onClose={() => setEntregaId(null)}
          onAnulada={buscar}
        />
      </IonContent>
    </IonPage>
  );
};

export default EppRegistro;
